/**
 * #523 — withdraw every one of YOUR resting Kalshi orders at one exact ladder
 * price level, in one bounded action.
 *
 * Sibling to `RestingOrderChip`: that chip cancels the ONE order it draws, this
 * control cancels the whole level the row represents. Both live in the ladder's
 * own-orders column; this one leads the group, on the outside edge away from the
 * chips, because it is the more destructive of the two.
 *
 * "The whole level" means exactly the orders already projected onto this row —
 * same market ticker, same side, same stack (buy rests on bids, sell on asks)
 * and the same venue-native price, sub-cent grain included. The target set is
 * never re-derived from a live list: it is FROZEN at the click that arms it, so
 * a poll landing between the trader reading the count and confirming it cannot
 * enlarge what gets destroyed (#272's contract, `_CONTEXT.md` #11). The count
 * and total contracts shown beside it are frozen with it — a readout that
 * drifts while the ID set does not is a readout that stops describing the set.
 *
 * @ai-danger THE LEVEL IS A PROP, AND IT CHANGES UNDER A MOUNTED CONTROL.
 * All three ladders key their rows by INDEX, so one new best bid — routine
 * inside the 2.5s arm window — hands this same component instance a DIFFERENT
 * level. Nothing may outlive that change: an armed snapshot that did would let
 * a control reading "cancel 2 at 40¢" submit the 41¢ IDs, which is a wrong-level
 * cancellation, not merely a wrong label. So the arm is dropped the moment the
 * identity changes, and the failure report is STAMPED with the level it belongs
 * to and rendered only against a matching one. The stamp is also what makes the
 * async write safe: a sweep resolving after a shift stores its result under its
 * OWN level, where it is simply not displayed, rather than re-attaching stale
 * failures to whatever rung the row now draws. (Ladders flap between two rungs
 * constantly, so a report whose level comes back is shown again — deliberate.)
 *
 * Guard behaviour follows the app-wide `confirmOrderCancel` preference, exactly
 * as the chip does:
 *
 *   • Cancel guard ON (default) — first click freezes and arms, showing the
 *     frozen order count and total contracts ("✕ 2 · 42?"); a second click
 *     within the arm window submits that frozen set. The arm auto-expires.
 *   • Cancel guard "One click" — the first click freezes and submits in the
 *     same tick, which is why a later-arriving order can never join the set.
 *
 * Every request goes through `executeBulkOrderCancel` — the ONE multi-order
 * sweep path in the app (`_CONTEXT.md` #9: "a second sweep implementation is
 * the defect"). It claims all IDs before the first DELETE, refreshes Kalshi
 * exactly once, and reports per order. A 2xx is `submitted`, never "cancelled":
 * the orders stay fenced, and this control stays inert, until an authoritative
 * Open Orders snapshot stops listing them (#241).
 *
 * On partial failure the control turns amber and wears the counts and the
 * venue's own message in its tooltip; clicking retries the FAILED ids only —
 * resending a submitted id re-DELETEs a cancellation that already worked
 * (`_CONTEXT.md` #14). The failed orders keep their own chips, still live.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useAuthStore } from '@/stores/authStore';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { useDisplaySettings } from '@/stores/displaySettings';
import { usePinnedKalshiOrderIds } from '@/hooks/usePinnedKalshiOrderIds';
import { isKalshiCancelPending } from '@/lib/trading/kalshiCancelLifecycle';
import {
  freezeCancelSnapshot,
  type FrozenCancelSnapshot,
  type OrderCancelTarget,
} from '@/lib/trading/gameOrderSelection';
import { executeBulkOrderCancel, type BulkCancelReport } from '@/lib/trading/bulkOrderCancel';
import { cancelPolyOrderViaRelay } from '@/lib/polymarket/relay';
import type { BidRowOrder } from './helpers';

/** Auto-disarm window for the two-step confirm (ms). Deliberately the same
 *  2.5s the per-order chip uses — one ladder, one arm rhythm. */
const ARM_TIMEOUT_MS = 2_500;

/**
 * The exact level a row represents, in the venue's own terms.
 *
 * `priceCents` is carried through verbatim at the centi-cent (0.01¢) grain the
 * order snapshot delivers. Re-rounding it here would let a 58.25¢ level match a
 * 58¢ order, which is the whole reason every ladder mirrors its ask rungs with
 * `mirrorCents` rather than a float `100 - p`.
 */
export interface LadderLevelIdentity {
  marketTicker: string;
  side: 'yes' | 'no';
  /** Which stack the row is: buy orders rest on bids, sell orders on asks. */
  action: 'buy' | 'sell';
  priceCents: number;
}

/** Everything that distinguishes one ladder level from another, as one value.
 *  Equality of this string is what "the row still means what it meant" means. */
function levelKey(level: LadderLevelIdentity): string {
  return `${level.marketTicker}|${level.side}|${level.action}|${level.priceCents}`;
}

function describeLevel(level: LadderLevelIdentity): string {
  return `${level.marketTicker} ${level.side.toUpperCase()} ${level.action} @ ${level.priceCents}¢`;
}

function targetsFor(orders: readonly BidRowOrder[]): OrderCancelTarget[] {
  return orders.map((o) => ({ key: `kalshi:${o.orderId}`, venue: 'kalshi' as const, orderId: o.orderId }));
}

function totalContracts(orders: readonly BidRowOrder[]): number {
  return orders.reduce((sum, o) => sum + Math.round(o.remainingCount), 0);
}

/** The approved set plus the numbers that described it when it was approved.
 *  All three are frozen together — the readout is part of the approval. */
interface ArmedLevel {
  levelKey: string;
  snapshot: FrozenCancelSnapshot;
  count: number;
  contracts: number;
  /** How many of the approved orders are pinned. */
  pinned: number;
}

/** A finished sweep, tagged with the level it was about. */
interface StampedReport {
  levelKey: string;
  report: BulkCancelReport;
}

/** #943 — pins protect only from Cancel All / Pull All. This control cancels
 *  exactly the level the trader clicked, pinned or not, and says so when a
 *  pinned order is among them. */
function pinnedIncludedNote(pinned: number): string {
  return pinned > 0 ? ` — includes ${pinned} pinned` : '';
}

export function CancelLevelControl({
  level,
  orders,
  compact = false,
}: {
  level: LadderLevelIdentity;
  orders: readonly BidRowOrder[];
  /** Fixed-width hosts (the narrow and compact derivative books, #1190) keep
   * a direct cancel beside the count or chips. Confirmation uses a bounded
   * overlay so its frozen summary cannot move the price columns. */
  compact?: boolean;
}) {
  const cancelOrders = useAccountStore((s) => s.cancelOrders);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  // The whole map, not a per-order slice: this control cares about N orders at
  // once. Safe because every lifecycle transition returns the map BY IDENTITY
  // when nothing changed, so a quiet poll cannot re-render the ladder.
  const cancelStates = useAccountStore((s) => s.kalshiCancelStates);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const keyMode = useAccountStore((s) => s.keyMode);
  const accountIdentity = useAuthStore((s) => JSON.stringify([s.status, s.user?.id, s.user?.orgId, s.user?.role]));
  const pinnedIds = usePinnedKalshiOrderIds();

  const [armed, setArmed] = useState<ArmedLevel | null>(null);
  const [report, setReport] = useState<StampedReport | null>(null);
  /** The level a sweep is in flight FOR, and how many ids it carries — so the
   *  running tooltip names the submitted set rather than whatever the row now
   *  holds. Null when idle. */
  const [running, setRunning] = useState<{ levelKey: string; count: number } | null>(null);
  // A ref, not the state above: React state lands a tick late and a double
  // click lands in the same tick (the useGameOpenOrdersModel rationale).
  const runningRef = useRef(false);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const [confirmPosition, setConfirmPosition] = useState({ top: 0, left: 0 });

  const key = `${accountIdentity}|${keyMode}|${levelKey(level)}`;
  const [seenKey, setSeenKey] = useState(key);

  // React's adjust-state-during-render pattern, NOT an effect: an effect runs a
  // tick after the row has already re-rendered with the new level, leaving one
  // frame in which the stale armed set is on screen AND clickable — a narrower
  // instance of the very bug this closes.
  if (seenKey !== key) {
    setSeenKey(key);
    // Any pending auto-disarm timer is left alone deliberately: all it does is
    // set the arm to null, which is a no-op once the arm is already gone.
    if (armed) setArmed(null);
  }
  if (armed && orders.length === 0) setArmed(null);

  const disarm = useCallback((restoreFocus = true) => {
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = null;
    if (restoreFocus && confirmationRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    setArmed(null);
  }, []);

  useDismissibleLayer({
    open: compact && armed !== null && orders.length > 0,
    refs: [triggerRef, confirmationRef],
    onDismiss: (reason) => disarm(reason === 'escape'),
    dismissOnFocusOut: true,
  });

  useEffect(() => {
    if (!compact || !armed) return;
    const dismiss = () => disarm();
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [compact, armed, disarm]);

  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current);
    },
    [],
  );

  if (orders.length === 0) return null;

  // Both are stamped, so a sweep or a failure belonging to another level simply
  // isn't this row's business — no clearing, no race.
  const myReport = report?.levelKey === key ? report : null;
  const failed = myReport?.report.failed ?? [];
  const retryMode = failed.length > 0;
  const isRunningHere = running?.levelKey === key;

  // Every order here is already fenced by this or another surface — there is
  // nothing left to submit, so the control is inert rather than falsely live.
  const allPending = orders.every((o) => isKalshiCancelPending(cancelStates[o.orderId]));
  // `running` (state), not `runningRef` (a ref never re-renders) — a sweep this
  // control started keeps it inert regardless of which level that sweep was for.
  const inert = running !== null || allPending;

  /** Freeze what THIS click approves: the failures being retried, or the whole
   *  level as it stands right now — plus the numbers that describe it. */
  const freezeNow = (): ArmedLevel => {
    const targets = retryMode ? failed.map((row) => row.target) : targetsFor(orders);
    const ids = new Set(targets.map((t) => t.orderId));
    return {
      levelKey: key,
      snapshot: freezeCancelSnapshot({ scope: 'level', label: describeLevel(level), targets }),
      count: targets.length,
      contracts: totalContracts(orders.filter((o) => ids.has(o.orderId))),
      pinned: targets.filter((t) => pinnedIds.has(t.orderId)).length,
    };
  };

  const submit = async (approved: ArmedLevel) => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning({ levelKey: approved.levelKey, count: approved.count });
    try {
      const next = await executeBulkOrderCancel(approved.snapshot, {
        // refreshAfter:false — the executor owns the single Kalshi refresh.
        cancelKalshiOrders: (ids) => cancelOrders(ids, { refreshAfter: false }),
        // The ladder projects Kalshi orders only, so the snapshot never carries a
        // Polymarket target and the executor's Poly leg early-returns. These two
        // deps are required by the shared contract and are unreachable here.
        requestPolymarketCancel: cancelPolyOrderViaRelay,
        refreshKalshi: () => refreshOrders(),
        refreshPolymarket: async () => {},
      });
      // Stored under the level it was ABOUT, not the level this row now draws.
      // Keep it only while it still says something the trader must act on; a
      // clean sweep leaves the orders fenced, which the pending state already
      // shows truthfully.
      setReport(
        next.failed.length > 0 || next.refreshErrors.length > 0
          ? { levelKey: approved.levelKey, report: next }
          : null,
      );
    } finally {
      runningRef.current = false;
      setRunning(null);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inert) return;

    if (confirmOrderCancel && !armed) {
      const next = freezeNow();
      if (next.count === 0) return;
      setArmed(next);
      if (compact) {
        const rect = e.currentTarget.getBoundingClientRect();
        setConfirmPosition({
          left: Math.max(8, Math.min(rect.left, window.innerWidth - 248)),
          top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 108)),
        });
      }
      if (armTimer.current) clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => disarm(), ARM_TIMEOUT_MS);
      return;
    }

    const approved = armed ?? freezeNow();
    disarm();
    if (approved.count === 0) return;
    void submit(approved);
  };

  const priceLabel = `${level.priceCents}¢`;
  const liveCount = orders.length;
  const liveContracts = totalContracts(orders);
  const livePinned = orders.filter((o) => pinnedIds.has(o.orderId)).length;
  const plural = (n: number) => (n === 1 ? 'order' : 'orders');

  const refreshNote = myReport?.report.refreshErrors.length
    ? ` (${myReport.report.refreshErrors.join('; ')})`
    : '';
  const title = isRunningHere
    ? `Canceling ${running!.count} ${plural(running!.count)} at ${priceLabel}…`
    : allPending
      ? `Canceling your orders at ${priceLabel} — waiting for Open Orders`
      : retryMode
        ? `${myReport!.report.submitted.length} submitted · ${failed.length} failed: ${
            failed[0]?.error ?? 'Cancel failed'
          } — click to retry failed${refreshNote}${pinnedIncludedNote(
            failed.filter((row) => pinnedIds.has(row.target.orderId)).length,
          )}`
        : armed
          ? `Click again to cancel ${armed.count} ${plural(armed.count)} (${armed.contracts}) at ${priceLabel}${pinnedIncludedNote(armed.pinned)}`
          : `Cancel your ${liveCount} ${plural(liveCount)} (${liveContracts}) at ${priceLabel}${refreshNote}${pinnedIncludedNote(livePinned)}`;

  // While armed, the accessible name must describe the FROZEN set — a poll that
  // adds an order post-arm would otherwise have assistive tech announce a count
  // the confirm will not submit.
  const label = armed
    ? `Confirm cancel of ${armed.count} ${plural(armed.count)} (${armed.contracts}) at ${priceLabel}`
    : retryMode
      ? `Retry ${failed.length} failed ${failed.length === 1 ? 'cancel' : 'cancels'} at ${priceLabel}`
      : `Cancel all ${liveCount} of your orders at ${priceLabel}`;

  return (
    <>
    <button
      ref={triggerRef}
      // `data-lt2-owner`: this button cancels, so it keeps every key, exactly
      // as the RestingOrderChip beside it does (#650). Without it, a bound
      // letter pressed on the focused control went to the aimed card (B4, as
      // on any bare button), so A/D placed an order from a cancel control; and
      // inside an aimed card's `data-lt2-hotkey-control` row (the Game Detail
      // spread/total book, #1190) Space and Enter went to the card as well.
      data-lt2-owner=""
      type="button"
      aria-label={label}
      // Inert is a real state here, not just a dimmed one: while a sweep awaits
      // authoritative removal the control ignores clicks, and assistive tech
      // must not announce it as actionable.
      aria-disabled={inert || undefined}
      disabled={inert}
      aria-expanded={compact ? Boolean(armed) : undefined}
      aria-haspopup={compact && confirmOrderCancel ? 'dialog' : undefined}
      title={title}
      onClick={handleClick}
      onMouseLeave={armed && !compact ? () => disarm() : undefined}
      className={cn(
        'shrink-0 rounded transition-colors',
        // #1070: the compact host is a fixed-width gutter, and a 3-digit
        // retry count beside 7-digit chips needs this padding back. The
        // pseudo-element keeps the hit area; it reaches right only, since the
        // control sits on the gutter's left edge.
        compact && retryMode
          ? "relative px-0 before:absolute before:inset-y-0 before:left-0 before:-right-1 before:content-['']"
          : 'px-0.5',
        inert
          ? 'animate-pulse cursor-default opacity-40'
          : 'cursor-pointer opacity-60 hover:bg-negative/15 hover:opacity-100',
        armed && 'bg-negative/20 text-negative opacity-100',
        retryMode && !inert && 'text-amber-400 opacity-100',
      )}
    >
      {armed && !compact ? (
        <span className="font-semibold">✕ {armed.count} · {armed.contracts}?</span>
      ) : retryMode ? (
        <span className="font-semibold">↻{failed.length}</span>
      ) : (
        '✕'
      )}
    </button>
    {compact && armed && createPortal(
      <div
        ref={confirmationRef}
        role="dialog"
        aria-modal="false"
        aria-label={`Confirm cancel at ${priceLabel}`}
        className="bg-card border-border fixed z-[100] w-60 max-w-[calc(100vw-16px)] rounded border p-2 text-xs shadow-lg"
        style={confirmPosition}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); disarm(); } }}
      >
        <p>Cancel {armed.count} {plural(armed.count)} · {armed.contracts} contracts at {priceLabel}?</p>
        <div className="mt-2 flex gap-2">
          <button type="button" className="text-negative rounded border px-2 py-1" onClick={handleClick}>Confirm cancel</button>
          <button type="button" className="rounded border px-2 py-1" onClick={() => disarm()}>Keep orders</button>
        </div>
      </div>, document.body,
    )}
    </>
  );
}
